import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import PreviewBanner from "./components/PreviewBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ROLE_LEARNER, ROLE_SUPER_ADMIN, ROLE_TENANT_ADMIN } from "./lib/constants";
import { Toaster } from "./components/ui/sonner";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const MagicLinkVerifyPage = lazy(() => import("./pages/MagicLinkVerifyPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const TenantsPage = lazy(() => import("./pages/admin/TenantsPage"));
const TenantExplorerPage = lazy(() => import("./pages/admin/TenantExplorerPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const CoursesPage = lazy(() => import("./pages/admin/CoursesPage"));
const CourseArrangePage = lazy(() => import("./pages/admin/CourseArrangePage"));
const MediaLibraryPage = lazy(() => import("./pages/admin/MediaLibraryPage"));
const CourseBuilderPage = lazy(() => import("./pages/admin/CourseBuilderPage"));
const TagManagerPage = lazy(() => import("./pages/admin/TagManagerPage"));
const RetroTagPage = lazy(() => import("./pages/admin/RetroTagPage"));
const RecordStudioPage = lazy(() => import("./pages/admin/RecordStudioPage"));
const TenantCoursePickerPage = lazy(() => import("./pages/admin/TenantCoursePickerPage"));
const AutoEmailsPage = lazy(() => import("./pages/admin/AutoEmailsPage"));
const AdminScenariosPage = lazy(() => import("./pages/admin/ScenariosPage"));
const ScenarioBuilderPage = lazy(() => import("./pages/admin/ScenarioBuilderPage"));
const ScenarioInsightsPage = lazy(() => import("./pages/admin/ScenarioInsightsPage"));
const AdminGamesPage = lazy(() => import("./pages/admin/GamesPage"));
const TermMatchBuilderPage = lazy(() => import("./pages/admin/TermMatchBuilderPage"));
const LearningPathsPage = lazy(() => import("./pages/admin/LearningPathsPage"));
const ContentFeedPage = lazy(() => import("./pages/admin/ContentFeedPage"));
const ResourceLibraryAdminPage = lazy(() => import("./pages/admin/ResourceLibraryAdminPage"));
const InboxPage = lazy(() => import("./pages/admin/InboxPage"));
const AnalyticsPage = lazy(() => import("./pages/admin/AnalyticsPage"));
const CertificatesPage = lazy(() => import("./pages/admin/CertificatesPage"));
const AdminLegalUpdatesPage = lazy(() => import("./pages/admin/LegalUpdatesPage"));
const AdminRegulationsPage = lazy(() => import("./pages/admin/RegulationsPage"));
const TenantTrackerPage = lazy(() => import("./pages/tenant-admin/TenantTrackerPage"));
const ComplianceChatPage = lazy(() => import("./pages/ComplianceChatPage"));
const LearnerLayout = lazy(() => import("./pages/learner/LearnerLayout"));
const LearnerDashboardPage = lazy(() => import("./pages/learner/LearnerDashboardPage"));
const CoursePlayerPage = lazy(() => import("./pages/learner/CoursePlayerPage"));
const CatalogPage = lazy(() => import("./pages/learner/CatalogPage"));
const LearnerSearchPage = lazy(() => import("./pages/learner/LearnerSearchPage"));
const LearnerScenariosPage = lazy(() => import("./pages/learner/ScenariosPage"));
const ScenarioPlayerPage = lazy(() => import("./pages/learner/ScenarioPlayerPage"));
const GamePlayerPage = lazy(() => import("./pages/learner/GamePlayerPage"));
const PathsPage = lazy(() => import("./pages/learner/PathsPage"));
const QuizPlayerPage = lazy(() => import("./pages/learner/QuizPlayerPage"));
const DailyFeedPage = lazy(() => import("./pages/learner/DailyFeedPage"));
const ResourceLibraryPage = lazy(() => import("./pages/learner/ResourceLibraryPage"));
const LeaderboardPage = lazy(() => import("./pages/learner/LeaderboardPage"));
const FeedbackAndRequestsPage = lazy(() => import("./pages/learner/FeedbackAndRequestsPage"));
const ProfilePage = lazy(() => import("./pages/learner/ProfilePage"));
const LearnerLegalUpdatesPage = lazy(() => import("./pages/learner/LegalUpdatesPage"));
const TenantAdminLayout = lazy(() => import("./pages/tenant-admin/TenantAdminLayout"));
const TeamPage = lazy(() => import("./pages/tenant-admin/TeamPage"));
const AssignCoursesPage = lazy(() => import("./pages/tenant-admin/AssignCoursesPage"));
const AssignPathsPage = lazy(() => import("./pages/tenant-admin/AssignPathsPage"));
const TenantReportsPage = lazy(() => import("./pages/tenant-admin/TenantReportsPage"));
const TenantBrandingPage = lazy(() => import("./pages/tenant-admin/TenantBrandingPage"));
const TagManagerPageTA = lazy(() => import("./pages/admin/TagManagerPage"));
const MediaLibraryPageTA = lazy(() => import("./pages/admin/MediaLibraryPage"));
const TenantDashboardPage = lazy(() => import("./pages/tenant-admin/TenantDashboardPage"));
const FeedbackTrackerPage = lazy(() => import("./pages/admin/FeedbackTrackerPage"));
const AnnouncementsPage = lazy(() => import("./pages/tenant-admin/AnnouncementsPage"));
const GuidesPage = lazy(() => import("./pages/admin/GuidesPage"));
const GuideBuilderPage = lazy(() => import("./pages/admin/GuideBuilderPage"));
const LearnerGuidesPage = lazy(() => import("./pages/learner/GuidesPage"));
const GuidePlayerPage = lazy(() => import("./pages/learner/GuidePlayerPage"));

const PageFallback = () => (
  <div data-testid="page-loading" className="flex min-h-[40vh] items-center justify-center text-sm text-[#4B5563]">
    Loading…
  </div>
);

const lazyRoute = (Component, props = {}) => (
  <ErrorBoundary>
    <Suspense fallback={<PageFallback />}>
      <Component {...props} />
    </Suspense>
  </ErrorBoundary>
);

const RootRedirect = () => {
  const { user } = useAuth();
  if (user === null) return <PageFallback />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === ROLE_SUPER_ADMIN) return <Navigate to="/admin/tenants" replace />;
  if (user.role === ROLE_TENANT_ADMIN) return <Navigate to="/tenant-admin/dashboard" replace />;
  return <Navigate to="/learn/dashboard" replace />;
};

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });

function App() {
  useEffect(() => {
    document.title = "CompliSun — Compliance Training";
  }, []);
  return (
    <div className="App">
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Toaster />
            <PreviewBanner />
            <Routes>
              <Route path="/" element={lazyRoute(RootRedirect)} />
              <Route path="/login" element={lazyRoute(LoginPage)} />
              <Route path="/auth/verify" element={lazyRoute(MagicLinkVerifyPage)} />
              <Route path="/reset-password" element={lazyRoute(ResetPasswordPage)} />

              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={[ROLE_SUPER_ADMIN]}>{lazyRoute(AdminLayout)}</ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="tenants" replace />} />
                <Route path="tenants" element={lazyRoute(TenantsPage)} />
                <Route path="tenants/:tenantId/view" element={lazyRoute(TenantExplorerPage)} />
                <Route path="users" element={lazyRoute(UsersPage)} />
                <Route path="courses" element={lazyRoute(CoursesPage)} />
                <Route path="courses/arrange" element={lazyRoute(CourseArrangePage)} />
                <Route path="tenant-courses" element={lazyRoute(TenantCoursePickerPage)} />
                <Route path="auto-emails" element={lazyRoute(AutoEmailsPage)} />
                <Route path="media" element={lazyRoute(MediaLibraryPage)} />
                <Route path="courses/:courseId/build" element={lazyRoute(CourseBuilderPage)} />
                <Route path="guides" element={lazyRoute(GuidesPage)} />
                <Route path="guides/:guideId/build" element={lazyRoute(GuideBuilderPage)} />
                <Route path="tags" element={lazyRoute(TagManagerPage)} />
                <Route path="retro-tag" element={lazyRoute(RetroTagPage)} />
                <Route path="studio" element={lazyRoute(RecordStudioPage)} />
                <Route path="scenarios" element={lazyRoute(AdminScenariosPage)} />
                <Route path="scenarios/:scenarioId/build" element={lazyRoute(ScenarioBuilderPage)} />
                <Route path="scenarios/:scenarioId/insights" element={lazyRoute(ScenarioInsightsPage)} />
                <Route path="games" element={lazyRoute(AdminGamesPage)} />
                <Route path="games/:gameId/build" element={lazyRoute(TermMatchBuilderPage)} />
                <Route path="paths" element={lazyRoute(LearningPathsPage)} />
                <Route path="courses/:courseId/preview" element={lazyRoute(CoursePlayerPage, { preview: true })} />
                <Route path="courses/:courseId/preview/quiz/:quizId" element={lazyRoute(QuizPlayerPage, { preview: true })} />
                <Route path="feed" element={lazyRoute(ContentFeedPage)} />
                <Route path="resources" element={lazyRoute(ResourceLibraryAdminPage)} />
                <Route path="inbox" element={lazyRoute(InboxPage)} />
                <Route path="certificates" element={lazyRoute(CertificatesPage)} />
                <Route path="legal-updates" element={lazyRoute(AdminLegalUpdatesPage)} />
                <Route path="regulations" element={lazyRoute(AdminRegulationsPage)} />
                <Route path="feedback" element={lazyRoute(FeedbackTrackerPage)} />
                <Route path="analytics" element={lazyRoute(AnalyticsPage)} />
              </Route>

              <Route
                path="/learn"
                element={
                  <ProtectedRoute allowedRoles={[ROLE_LEARNER]}>{lazyRoute(LearnerLayout)}</ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={lazyRoute(LearnerDashboardPage)} />
                <Route path="catalog" element={lazyRoute(CatalogPage)} />
                <Route path="guides" element={lazyRoute(LearnerGuidesPage)} />
                <Route path="guides/:guideId" element={lazyRoute(GuidePlayerPage)} />
                <Route path="search" element={lazyRoute(LearnerSearchPage)} />
                <Route path="scenarios" element={lazyRoute(LearnerScenariosPage)} />
                <Route path="scenarios/:scenarioId" element={lazyRoute(ScenarioPlayerPage)} />
                <Route path="games/:gameId" element={lazyRoute(GamePlayerPage)} />
                <Route path="paths" element={lazyRoute(PathsPage)} />
                <Route path="courses/:courseId" element={lazyRoute(CoursePlayerPage)} />
                <Route path="courses/:courseId/quiz/:quizId" element={lazyRoute(QuizPlayerPage)} />
                <Route path="feed" element={lazyRoute(DailyFeedPage)} />
                <Route path="resources" element={lazyRoute(ResourceLibraryPage)} />
                <Route path="updates" element={lazyRoute(LearnerLegalUpdatesPage)} />
                <Route path="chat" element={lazyRoute(ComplianceChatPage)} />
                <Route path="leaderboard" element={lazyRoute(LeaderboardPage)} />
                <Route path="feedback" element={lazyRoute(FeedbackAndRequestsPage)} />
                <Route path="profile" element={lazyRoute(ProfilePage)} />
              </Route>

              <Route
                path="/tenant-admin"
                element={
                  <ProtectedRoute allowedRoles={[ROLE_TENANT_ADMIN]}>{lazyRoute(TenantAdminLayout)}</ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={lazyRoute(TenantDashboardPage)} />
                <Route path="team" element={lazyRoute(TeamPage)} />
                <Route path="courses" element={lazyRoute(AssignCoursesPage)} />
                <Route path="paths" element={lazyRoute(AssignPathsPage)} />
                <Route path="legal-updates" element={lazyRoute(AdminLegalUpdatesPage)} />
                <Route path="tracker" element={lazyRoute(TenantTrackerPage)} />
                <Route path="chat" element={lazyRoute(ComplianceChatPage)} />
                <Route path="reports" element={lazyRoute(TenantReportsPage)} />
                <Route path="feedback" element={lazyRoute(FeedbackTrackerPage)} />
                <Route path="announcements" element={lazyRoute(AnnouncementsPage)} />
                <Route path="branding" element={lazyRoute(TenantBrandingPage)} />
                <Route path="taxonomy" element={lazyRoute(TagManagerPageTA)} />
                <Route path="media" element={lazyRoute(MediaLibraryPageTA)} />
                <Route path="studio" element={lazyRoute(RecordStudioPage)} />
                <Route path="courses/:courseId/build" element={lazyRoute(CourseBuilderPage)} />
                <Route path="courses/:courseId/preview" element={lazyRoute(CoursePlayerPage, { preview: true })} />
                <Route path="courses/:courseId/preview/quiz/:quizId" element={lazyRoute(QuizPlayerPage, { preview: true })} />
                <Route path="guides" element={lazyRoute(GuidesPage)} />
                <Route path="guides/:guideId/build" element={lazyRoute(GuideBuilderPage)} />
                <Route path="resources" element={lazyRoute(ResourceLibraryAdminPage)} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
        </QueryClientProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
